import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import { ASSETS } from '../config/assets';
import { Smartphone, Palette, CreditCard, CheckCircle, Cpu } from 'lucide-react';

const HardwarePage = () => {
  const { t, language } = useLanguage();

  const features = [
    {
      icon: CreditCard,
      title: t('hardware.feature1Title'),
      desc: t('hardware.feature1Desc'),
    },
    {
      icon: Smartphone,
      title: t('hardware.feature2Title'),
      desc: t('hardware.feature2Desc'),
    },
    {
      icon: Palette,
      title: t('hardware.feature3Title'),
      desc: t('hardware.feature3Desc'),
    },
  ];

  const zeroIntegration = language === 'fr' ? [
    'Pas de changements à votre système existant',
    'Déployez à côté de vos opérations actuelles',
    'Pas de formation IT complexe requise',
    'Cartes prêtes à l\'emploi dès réception'
  ] : [
    'No changes to your existing system',
    'Deploys alongside your current operations',
    'No complex IT training required',
    'Cards ready to use out of the box'
  ];

  return (
    <div data-testid="hardware-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />
      <BackToHome />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Smartphone className="w-8 h-8 text-wine" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-wine">
                  {t('hardware.title')}
                </span>
              </div>
              <h1 
                data-testid="hardware-title"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
              >
                {t('hardware.subtitle')}
              </h1>
              <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-8">
                {t('hardware.heroDesc')}
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  data-testid="hardware-cta"
                  className="btn-demo-cta text-white rounded-lg px-8 py-6"
                >
                  {t('nav.requestDemo')}
                </Button>
              </Link>
            </div>
            <div>
              <img
                src={ASSETS.PHYSICAL_TAP}
                alt="Tap Engine Card interaction"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-square"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Zero Integration Section */}
      <section className="py-16 bg-[#1A1A1A] border-t border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Cpu className="w-6 h-6 text-wine" strokeWidth={1.5} />
            <span className="text-sm font-bold uppercase tracking-wider text-white/70">
              {language === 'fr' ? 'Zéro Intégration POS Requise' : 'Zero POS Integration Required'}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {zeroIntegration.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-white/80 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                data-testid={`hardware-feature-${idx}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:-translate-y-1 hover:border-wine/30 transition-all duration-300"
              >
                <div className="p-3 bg-wine/10 rounded-xl w-fit mb-6">
                  <feature.icon className="w-6 h-6 text-wine" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HardwarePage;
